/**
 * Rectangle Drawing Controller - handles rectangle drawing operations
 * Manages rectangle drawing mode on the map
 */
import {Subject} from 'rxjs';
import {BaseDrawController} from './base-draw.controller';

export class DrawRectangleController extends BaseDrawController {
  private rectangleDrawingEnabled = false;
  private currentRectangle: any = null;
  private isDrawingRectangle = false;
  private rectangleStartPoint: any = null;
  private rectangleLayer: any = null;

  // Event emitter for shape creation
  public shapeCreated$ = new Subject<{
    type: 'rectangle';
    coordinates: [number, number][];
    center: { lat: number; lng: number };
    layer: any;
  }>();

  constructor(mapInstance: any) {
    super(mapInstance);
  }

  /**
   * Enable rectangle drawing mode
   */
  public enable(): void {
    if (this.rectangleDrawingEnabled) return;

    this.rectangleDrawingEnabled = true;
    this.enableDrawingMode();

    // Attach event listeners
    this.mapClickHandler = (e: any) => this.handleRectangleClick(e);
    this.mapMouseMoveHandler = (e: any) => this.handleRectangleMouseMove(e);

    this.map.on('click', this.mapClickHandler);
    this.map.on('mousemove', this.mapMouseMoveHandler);
  }

  /**
   * Disable rectangle drawing mode
   */
  public disable(): void {
    this.rectangleDrawingEnabled = false;
    this.cancelRectangleDrawing();
    this.disableDrawingMode();

    // Remove event listeners
    this.removeMapEventListeners([
      { event: 'click', handler: this.mapClickHandler },
      { event: 'mousemove', handler: this.mapMouseMoveHandler }
    ]);
    this.mapClickHandler = null;
    this.mapMouseMoveHandler = null;
  }

  /**
   * Check if rectangle drawing is active
   */
  public override isActive(): boolean {
    return this.rectangleDrawingEnabled;
  }

  /**
   * Handle rectangle drawing click
   */
  private handleRectangleClick(e: any): void {
    if (!this.rectangleDrawingEnabled) return;

    if (!this.rectangleStartPoint) {
      // First click - set start point
      this.rectangleStartPoint = e.latlng;
      this.isDrawingRectangle = true;
    } else {
      // Second click - create rectangle
      const endPoint = e.latlng;
      this.createRectangle(this.rectangleStartPoint, endPoint);
      this.rectangleStartPoint = null;
      this.isDrawingRectangle = false;
    }
  }

  /**
   * Handle rectangle mouse move for preview
   */
  private handleRectangleMouseMove(e: any): void {
    if (!this.rectangleDrawingEnabled || !this.isDrawingRectangle || !this.rectangleStartPoint) return;

    // Remove previous preview rectangle
    if (this.rectangleLayer) {
      this.map.removeLayer(this.rectangleLayer);
    }

    // Create preview rectangle
    const bounds = (window as any).L.latLngBounds(this.rectangleStartPoint, e.latlng);
    this.rectangleLayer = (window as any).L.rectangle(bounds, {
      color: '#3388ff',
      weight: 2,
      opacity: 0.6,
      fill: true,
      fillColor: '#3388ff',
      fillOpacity: 0.2,
      dashArray: '5, 5'
    }).addTo(this.map);

    // this.updateRealTimeMeasurements(this.rectangleStartPoint, e.latlng);
  }

  /**
   * Update real-time measurement labels during rectangle drawing

  /**
   * Clear all measurement labels
   */

  /**
   * Create and store rectangle
   */
  private createRectangle(startPoint: any, endPoint: any): void {
    const bounds = (window as any).L.latLngBounds(startPoint, endPoint);

    if (this.rectangleLayer) {
      this.map.removeLayer(this.rectangleLayer);
      this.rectangleLayer = null;
    }

    const rectangle = (window as any).L.rectangle(bounds, {
      color: '#3388ff',
      weight: 2,
      opacity: 0.8,
      fill: true,
      fillColor: '#3388ff',
      fillOpacity: 0.3
    }).addTo(this.map);

    this.currentRectangle = {
      layer: rectangle,
      bounds: bounds,
      coordinates: [
        [startPoint.lat, startPoint.lng],
        [endPoint.lat, endPoint.lng]
      ]
    };

    // Emit shape created event
    this.shapeCreated$.next({
      type: 'rectangle',
      coordinates: [
        [startPoint.lat, startPoint.lng],
        [endPoint.lat, endPoint.lng]
      ],
      center: {
        lat: (startPoint.lat + endPoint.lat) / 2,
        lng: (startPoint.lng + endPoint.lng) / 2
      },
      layer: rectangle
    });

    // Add to drawn items if available
    if (this.drawnItems) {
      this.drawnItems.addLayer(rectangle);
    }
  }

  /**
   * Cancel current rectangle drawing
   */
  private cancelRectangleDrawing(): void {
    if (this.rectangleLayer) {
      this.map.removeLayer(this.rectangleLayer);
      this.rectangleLayer = null;
    }
    if (this.currentRectangle && this.currentRectangle.layer) {
      this.map.removeLayer(this.currentRectangle.layer);
    }

    this.currentRectangle = null;
    this.rectangleStartPoint = null;
    this.isDrawingRectangle = false;
  }

  /**
   * Get current rectangle
   */
  public getCurrentRectangle(): any {
    return this.currentRectangle;
  }

  // Edit mode methods are now inherited from BaseDrawController
  // enableEditMode(), disableEditMode(), addEditButtons(), removeEditButtons()
  // undoLastShape(), removeShape(), registerDrawnShape() are all in the parent class
}
